import { TableNavigation, TablePaper } from 'components/common';
import { ListTable } from 'components/core';
import { matchEnabledColumns } from 'components/core/ColumnPanel/utils';
import { useContext, useMemo } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { ORDER_TABLE_HEADERS, rowLayout } from 'utils/constants/Order';
import {
  ColumnContext,
  FilterContext,
  ListContext,
  ViewContext,
} from 'utils/context';
import { useDetailsRedirect } from 'utils/hooks';

import { EntityEnum } from '@project44/entity-ui-utils';
import { CSS } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { IOrder } from '@project44-models/shipment-list';

interface IOrdersTable {
  css: CSS;
  doSearch: () => void;
}

function OrdersTable({ css, doSearch }: IOrdersTable) {
  const { t } = useTranslation();
  const handleRedirect = useDetailsRedirect();
  const { clearFilters, hasFilters } = useContext(FilterContext);
  const { savedOrders } = useContext(ViewContext);
  const { displayOptions } = useContext(ColumnContext);

  const { doSearchOrders, isErrorOrders, isLoadingOrders, ordersData } =
    useContext(ListContext);

  const [headers, rows] = useMemo(
    () =>
      matchEnabledColumns(
        ORDER_TABLE_HEADERS,
        rowLayout,
        displayOptions?.activeColumns || [],
      ),
    [displayOptions?.activeColumns],
  );

  const onClear = () => {
    clearFilters(ALL_FILTERS);
    doSearch();
  };

  const onPageChange = (page: number) => {
    doSearchOrders({ page, size: 20 });
  };

  const onRowclick = ({ orderId }: IOrder) => {
    handleRedirect(EntityEnum.order, orderId);
  };

  return (
    <TablePaper css={css}>
      <TableNavigation
        title={t('core.orders')}
        resultsCount={ordersData.paginationInfo?.total}
        css={{ marginBottom: '$medium' }}
        isLoading={isLoadingOrders}
      />
      <ListTable
        title={t('core.orders')}
        css={{ flex: '1 1 0' }}
        cssTable={{ flex: '1 1 0' }}
        data={ordersData?.results || []}
        doSearch={doSearch}
        handleClear={onClear}
        headers={headers}
        isError={isErrorOrders}
        isLoading={isLoadingOrders || savedOrders.isLoading}
        pagination={{ ...ordersData.paginationInfo, onPageChange }}
        rowClick={onRowclick}
        rowLayout={rows}
        showClearButton={hasFilters(ALL_FILTERS)}
      />
    </TablePaper>
  );
}

export default OrdersTable;
